// src/Screens/Sections/ApplicationForm.tsx

import React, { useState, useEffect } from 'react';
import { Label } from '../Components/Label';
import { Input } from '../Components/Input';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { collection, addDoc, getDoc, doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase';
import { FileUpload } from '../Components/File-upload';
import ProgressBar from '../Components/ProgressBar';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import logo from "../../Assets/logo.png";
import BudgetSlider from '../Components/BudgetSlider';
import { ButtonsCard } from '../Components/ButtonsCard';
import { MultiStepLoader } from '../Components/Multi-step-loader';

// Import your icons
import BuyingHomeIcon from '../../Assets/buying-home-icon.png';
import RefinancingHomeIcon from '../../Assets/refinancing-home-icon.jpg';

const GradientBackground = styled.div`
  background: linear-gradient(0deg, rgba(133,212,213,1) 13%, rgba(207,244,149,1) 100%);
  background-size: 400% 400%;
  animation: gradient 15s ease infinite;
  
  @keyframes gradient {
    0% {
      background-position: 50% 0%;
    }
    50% {
      background-position: 50% 100%;
    }
    100% {
      background-position: 50% 0%;
    }
  }
`;

type Question = {
  label: string;
  name: string;
  type: string;
  placeholder?: string;
  required?: boolean;
  options?: { value: string; label: string }[];
  component?: 'input' | 'select' | 'file' | 'slider';
  condition?: (formData: any) => boolean;
  min?: number;
  max?: number;
  step?: number;
};

type QuestionCategory = {
  name: string;
  questions: Question[];
};

const questionCategories: QuestionCategory[] = [
  {
    name: "Basic Info",
    questions: [
      {
        label: "Let's start by asking you a few questions and get started on this new journey with us!",
        // name: 'start',
        // type: 'text',
        // placeholder: 'Start',
        // required: true,
        // component: 'input',
      },
      {
        label: 'What is your first name?',
        name: 'firstName',
        type: 'text',
        placeholder: 'John',
        required: true,
        component: 'input',
      },
      {
        label: 'What is your last name?',
        name: 'lastName',
        type: 'text',
        placeholder: 'Doe',
        required: true,
        component: 'input',
      },
      {
        label: 'What is your email address?',
        name: 'email',
        type: 'email',
        placeholder: 'john.doe@example.com',
        required: true,
        component: 'input',
      },
      {
        label: 'What is your phone number?',
        name: 'phoneNumber',
        type: 'tel',
        placeholder: '+1 (555) 000-0000',
        required: true,
        component: 'input',
      },
      {
        label: 'When were you born?',
        name: 'dateOfBirth',
        type: 'date',
        required: true,
        component: 'input',
      },
      {
        label: 'What is your marital status?',
        name: 'maritalStatus',
        type: 'text',
        required: true,
        component: 'select',
        options: [
          { value: '', label: 'Select marital status' },
          { value: 'single', label: 'Single' },
          { value: 'married', label: 'Married' },
          { value: 'divorced', label: 'Divorced' },
          { value: 'widowed', label: 'Widowed' },
        ],
      },
      // Conditional Questions
      {
        label: "What is your spouse's name?",
        name: 'spouseName',
        type: 'text',
        placeholder: 'Jane Doe',
        required: true,
        component: 'input',
        condition: (formData) =>
          formData.maritalStatus && formData.maritalStatus !== 'single',
      },
      {
        label: "What is your spouse's contact information?",
        name: 'spouseContact',
        type: 'text',
        placeholder: '+1 (555) 123-4567',
        required: true,
        component: 'input',
        condition: (formData) =>
          formData.maritalStatus && formData.maritalStatus !== 'single',
      },
    
    ],
  },
  {
    name: "Loan Purpose",
    questions: [
      {
        label: "I am applying for :",
        name: "loanPurpose",
        type: "select",
        required: true,
        component: "select",
        options: [
          { value: "", label: "Select purpose" },
          { value: "buying", label: "Buying a new home" },
          { value: "refinancing", label: "Refinancing a home" },
        ],
      },
    ],
  },
  {
    name: "Financial Info",
    questions: [
      {
        label: "Now let's get some financial information from you so that we can prepare your financing journey in an efficient way",
      },
      {
        label: 'What is your budget?',
        name: 'budget',
        type: 'slider',
        required: true,
        component: 'slider',
        min: 50000,
        max: 1000000,
        step: 10000,
      },
      {
        label: 'What is your annual income?(just include numbers)',
        name: 'annualIncome',
        type: 'number',
        placeholder: '$50,000',
        required: true,
        component: 'input',
      },
    ],
  },
  {
    name: "Employment Info",
    questions: [
      {
        label: "Now tell us a little bit about your employment history if you have any or if you are self-employed as an entrepreneur or a business owner",
        name: 'occupation',
        type: 'text',
        required: true,
        component: 'select',
        options: [
          { value: '', label: 'Select occupation' },
          { value: 'w2', label: 'W2 Employee' },
          { value: '1099', label: '1099 Contractor' },
          { value: 'self-employed', label: 'Self-Employed' },
          { value: 'business-owner', label: 'Business Owner' },
        ],
      },
      {
        label: 'Who is your employer?',
        name: 'employerName',
        type: 'text',
        placeholder: 'Enter your employer name',
        required: true,
        component: 'input',
        condition: (formData) => formData.occupation === 'w2' || formData.occupation === '1099',
      },
      {
        label: 'What is your position?',
        name: 'position',
        type: 'text',
        placeholder: 'Software Engineer',
        required: true,
        component: 'input',
        condition: (formData) => formData.occupation === 'w2' || formData.occupation === '1099',
      },
      {
        label: 'What is the name of your business?',
        name: 'businessName',
        type: 'text',
        placeholder: 'Enter your business name',
        required: true,
        component: 'input',
        condition: (formData) => formData.occupation === 'business-owner',
      },
      {
        label: 'What type of business do you own?',
        name: 'businessType',
        type: 'text',
        placeholder: 'e.g., Consulting, Retail, Technology',
        required: true,
        component: 'input',
        condition: (formData) =>  formData.occupation === 'business-owner',
      },
      {
        label: 'What is your area of expertise?',
        name: 'areaOfExpertise',
        type: 'text',
        placeholder: 'e.g., Software Development, Marketing, Finance',
        required: true,
        component: 'input',
        condition: (formData) => formData.occupation === 'self-employed',
      }
    ],
  },
  {
    name: "Documents",
    questions: [
      {
        label: "You are almost done! We just need a few documents from you to finalize your application",
      },
      {
        label: 'Please upload your recent pay stubs.',
        name: 'payStubs',
        type: 'file',
        component: 'file',
      },
      {
        label: 'Please upload your Social Security card.',
        name: 'socialSecurityCard',
        type: 'file',
        component: 'file',
      },
      {
        label: 'Please upload your driver license.',
        name: 'driverLicense',
        type: 'file',
        component: 'file',
      },
      {
        label: 'Please upload proof of residency.',
        name: 'proofOfResidency',
        type: 'file',
        component: 'file',
      },
    ],
  },
];

const LoanPurposeCard = ({ value, label, icon, onClick, isSelected }) => (
  <motion.div
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
    onClick={() => onClick(value)}
    className={`flex flex-col items-center justify-center p-6 bg-lime-400 rounded-lg cursor-pointer ${
      isSelected ? 'ring-2 ring-indigo-600' : ''
    }`}
  >
    <img src={icon} alt={label} className="w-16 h-16 mb-4" />
    <span className="text-lg font-medium text-gray-900">{label}</span>
  </motion.div>
);

const getVisibleQuestions = (category: QuestionCategory, formData: any) => {
  return category.questions.filter(question => 
    !question.condition || question.condition(formData)
  );
};

const MortgageApplicationForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [isEditing, setIsEditing] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [formData, setFormData] = useState<any>({
    status: 'pending',
    submittedAt: null,
  });
  const [selectedFiles, setSelectedFiles] = useState<any>({});
  const [fadeIn, setFadeIn] = useState(true);
  const [loading, setLoading] = useState(false);

  const loadingStates = [
    { text: "Submitting your application" },
    { text: "Verifying your information" },
    { text: "Processing your documents" },
    { text: "Finalizing your application" },
    { text: "Preparing your status page" },
  ];

  // State for scroll accumulation (if you decide to keep scroll navigation)
  const [scrollDeltaY, setScrollDeltaY] = useState(0);
  const SCROLL_THRESHOLD = 200;

  const currentCategory = questionCategories[currentCategoryIndex];
  const currentQuestion = currentCategory.questions[currentQuestionIndex];

  const totalQuestions = questionCategories.reduce((sum, category) => sum + category.questions.length, 0);
  const completedQuestions = questionCategories.slice(0, currentCategoryIndex).reduce((sum, category) => sum + category.questions.length, 0) + currentQuestionIndex;
  const progress = (completedQuestions / totalQuestions) * 100;

  const isFirstQuestion = currentCategoryIndex === 0 && currentQuestionIndex === 0;

  useEffect(() => {
    const fetchApplicationData = async () => {
      if (id) {
        setIsEditing(true);
        setApplicationId(id);
        const docRef = doc(db, 'applications', id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setFormData(docSnap.data());
          
          // Set the current category and question index based on the existing data
          const lastCompletedCategory = questionCategories.findIndex(category => 
            category.questions.some(question => !docSnap.data()[question.name])
          );
          setCurrentCategoryIndex(lastCompletedCategory === -1 ? questionCategories.length - 1 : lastCompletedCategory);
          
          const lastCompletedQuestion = questionCategories[currentCategoryIndex].questions.findIndex(question => 
            !docSnap.data()[question.name]
          );
          setCurrentQuestionIndex(lastCompletedQuestion === -1 ? 0 : lastCompletedQuestion);
        }
      }
    };

    fetchApplicationData();
  }, [id]);

  // Handle input changes
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevState: any) => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // Handle file changes
  const handleFileChange = (name: string) => (files: File[]) => {
    setSelectedFiles((prevState: any) => ({
      ...prevState,
      [name]: files,
    }));
  };

  // Handle keydown for 'Enter' key navigation
  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      nextQuestion();
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    setLoading(true);
    
    // Upload files to Firebase Storage
    const fileUploadPromises = Object.keys(selectedFiles).map(
      async (key: string) => {
        if (selectedFiles[key] && selectedFiles[key].length > 0) {
          const file = selectedFiles[key][0];
          const storageRef = ref(
            storage,
            `applications/${formData.email}/${key}/${file.name}`
          );
          await uploadBytes(storageRef, file);
          const downloadURL = await getDownloadURL(storageRef);
          return { [key]: downloadURL };
        }
        return null;
      }
    );

    const fileURLsArray = await Promise.all(fileUploadPromises);
    const fileURLs = fileURLsArray.reduce((acc, curr) => {
      return curr ? { ...acc, ...curr } : acc;
    }, {});

    // Combine formData with file URLs
    const applicationData = {
      ...formData,
      ...fileURLs,
      status: 'pending',
      submittedAt: new Date(),
    };

    try {
      let docId;
      if (isEditing) {
        await updateDoc(doc(db, 'applications', applicationId), applicationData);
        console.log('Application updated successfully');
        docId = applicationId;
      } else {
        const docRef = await addDoc(collection(db, 'applications'), applicationData);
        console.log('Application submitted with ID: ', docRef.id);
        docId = docRef.id;
      }
      
      // Simulate a delay before redirecting (adjust as needed)
      setTimeout(() => {
        setLoading(false);
        navigate(`/application-status/${docId}`);
      }, 10000); // 10 seconds delay
    } catch (error) {
      console.error('Error submitting/updating application: ', error);
      setLoading(false);
      alert(
        'There was an error submitting/updating your application. Please try again.'
      );
    }
  };

  // Navigate to the next question
  const nextQuestion = () => {
    const visibleQuestions = getVisibleQuestions(currentCategory, formData);
    if (currentQuestionIndex < visibleQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else if (currentCategoryIndex < questionCategories.length - 1) {
      setCurrentCategoryIndex(currentCategoryIndex + 1);
      setCurrentQuestionIndex(0);
    } else {
      // If it's the last question, submit the form
      handleSubmit();
    }
  };

  // Navigate to the previous question
  const prevQuestion = () => {
    const visibleQuestions = getVisibleQuestions(currentCategory, formData);
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    } else if (currentCategoryIndex > 0) {
      setCurrentCategoryIndex(currentCategoryIndex - 1);
      const prevCategoryQuestions = getVisibleQuestions(questionCategories[currentCategoryIndex - 1], formData);
      setCurrentQuestionIndex(prevCategoryQuestions.length - 1);
    }
  };

  // Handle changes in formData to adjust currentQuestionIndex if necessary
  useEffect(() => {
    const visibleQuestions = getVisibleQuestions(currentCategory, formData);
    if (currentQuestionIndex >= visibleQuestions.length) {
      setCurrentQuestionIndex(visibleQuestions.length - 1);
    }
  }, [formData, currentCategory, currentQuestionIndex]);

  // Render the current question with conversational UI
  const renderCurrentQuestion = () => {
    const visibleQuestions = getVisibleQuestions(currentCategory, formData);
    const currentQuestion = visibleQuestions[currentQuestionIndex];  
    if (!currentQuestion) return null;

    if (currentQuestion.name === 'loanPurpose') {
      return (
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-normal text-gray-900 mb-6 font-philosopher">{currentQuestion.label}</h2>
          <div className="grid grid-cols-2 gap-6">
            <LoanPurposeCard
              value="buying"
              label="Buying a new home"
              icon={BuyingHomeIcon}
              onClick={(value) => handleChange({ target: { name: 'loanPurpose', value } })}
              isSelected={formData.loanPurpose === 'buying'}
            />
            <LoanPurposeCard
              value="refinancing"
              label="Refinancing a home"
              icon={RefinancingHomeIcon}
              onClick={(value) => handleChange({ target: { name: 'loanPurpose', value } })}
              isSelected={formData.loanPurpose === 'refinancing'}
            />
          </div>
        </div>
      );
    }

    if (currentQuestion.component === 'slider') {
      return (
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl font-normal text-gray-900 mb-6 font-philosopher">{currentQuestion.label}</h2>
          <BudgetSlider
            min={currentQuestion.min}
            max={currentQuestion.max}
            step={currentQuestion.step}
            value={formData[currentQuestion.name] || currentQuestion.min}
            onChange={(value) => handleChange({ target: { name: currentQuestion.name, value } })}
          />
        </div>
      );
    }

    const commonProps = {
      id: currentQuestion.name,
      name: currentQuestion.name,
      value: formData[currentQuestion.name] || '',
      onChange: handleChange,
      onKeyDown: handleKeyDown,
      required: currentQuestion.required,
    };

    return (
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
        <h2 className="text-4xl font-normal text-gray-900 mb-2 font-philosopher">{currentQuestion.label}</h2>
        {/* <p className="text-gray-600">Please provide the requested information below.</p> */}
        </div>
        <div className="p-6 rounded-lg shadow-sm">
          {currentQuestion.component === 'input' && (
            <Input
              {...commonProps}
              type={currentQuestion.type}
              placeholder={currentQuestion.placeholder}
              className="w-full text-lg"
            />
          )}
          {currentQuestion.component === 'select' && (
            <select
              {...commonProps}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-lg"
            >
              {currentQuestion.options?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          )}
          {currentQuestion.component === 'file' && (
            <FileUpload
            onChange={handleFileChange(currentQuestion.name)}
            initialFile={formData[currentQuestion.name]}
            fieldName={currentQuestion.name}
          />
          )}
        </div>
      </div>
    );
  };

  const formVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        type: "spring",
        stiffness: 100,
        damping: 15,
        mass: 0.7,
      }
    }
  };

  return (
    <GradientBackground className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
          <button
              onClick={() => navigate('/')}
              className="text-indigo-600 hover:text-indigo-800 transition-colors"
            >
           <motion.img 
            src={logo} 
            alt="Logo" 
            style={{ height: "80px", filter: "brightness(1.5)" }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            transition={{ type: "spring", stiffness: 400, damping: 17 }}
          />
            
            </button>
          </div>
          <h1 className="text-5xl font-bold font-philosopher sans-serif text-gray-900">
            {isEditing ? 'Edit Application' : 'Home Financing'}
          </h1>
          <div className="w-24"></div> {/* Spacer for alignment */}
        </div>
      </header>

      {/* Main content */}
      <main style={{ padding: '20px', paddingTop: '40px', paddingBottom: '40px', width: '100%' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <motion.div 
            className="bg-white shadow-lg rounded-lg overflow-hidden"
            initial="hidden"
            animate="visible"
            variants={formVariants}
          >
            {/* Progress Bar */}
            <ProgressBar 
              categories={questionCategories.map(cat => cat.name)} 
              currentCategory={currentCategoryIndex}
            />

            <div style={{ padding: '20px', paddingTop: '20px', paddingBottom: '20px', width: '100%' }}>
              <form onSubmit={(e) => e.preventDefault()} className="space-y-8">
                <motion.div
                  className={`transition-opacity duration-300 ease-in-out ${
                    fadeIn ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  {renderCurrentQuestion()}
                </motion.div>

                <div className="flex justify-between mt-8">
              {!isFirstQuestion && (
                <ButtonsCard onClick={prevQuestion} className="w-auto h-auto">
                  <button className="p-[3px] relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-[#393ce9] to-[#55c4f7] rounded-lg" />
                    <div className="px-8 py-2 bg-white rounded-[6px] relative group transition duration-200 text-black hover:bg-transparent hover:text-white">
                      &larr; Previous
                    </div>
                  </button>
                </ButtonsCard>
              )}
              {isFirstQuestion && <div></div>} {/* Spacer for alignment when there's no Previous button */}
              
              <ButtonsCard onClick={nextQuestion} className="w-auto h-auto">
                <button className="p-[3px] relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-[#393ce9] to-[#55c4f7] rounded-lg" />
                  <div className="px-8 py-2 bg-white rounded-[6px] relative group transition duration-200 text-black hover:bg-transparent hover:text-white">
                    {currentQuestionIndex < currentCategory.questions.length - 1 || currentCategoryIndex < questionCategories.length - 1 ? 'Next' : 'Submit'}
                  </div>
                </button>
              </ButtonsCard>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      </main>

      {/* Add the MultiStepLoader component */}
      <MultiStepLoader 
        loadingStates={loadingStates} 
        loading={loading} 
        duration={2500}
        className="font-philosopher text-3xl" />
        </GradientBackground>
  );
};

export default MortgageApplicationForm;